import React, { useMemo, useState, useEffect } from "react";
import { orderBy } from "lodash";
import { useNavigate } from "react-router";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Badge from "@mui/material/Badge";
import Footer from "../../../../../../components/wizard/Footer/Footer";
import useInventoryItemsFilter from "../../../../../../hooks/useInventoryItemsFilter";
import Item from "../../../../../../components/wizard/Item/Item";
import ItemInfo from "./ItemInfo";
import ItemActions from "./ItemActions";
import { getWO } from "../../../../utils";
import Header from "../../../../../../components/wizard/Header";
import useQueryParams from "../../../../../../hooks/useQueryParams";
import ItemDetails from "./ItemDetails/ItemDetails";
import { getOutboundBillingType } from "src/utils/billingHelpers";
import BillingQuantitiesModal from "src/components/capture/ReceivingWizard/Shipment/Billing/BillingQuantitiesModal";
import {
  BY_THE_PALLET,
  BY_THE_PALLET_WAREHOUSE_BUILT,
  BY_THE_UNIT_ITEM,
  BY_THE_VAULT,
  TIME_AND_MATERIALS_BLENDED,
  TIME_AND_MATERIALS_ITEMIZED,
} from "src/components/project/settings/utils/billingTypes";
import SearchButton from "../../../../../../components/wizard/SearchButton";
import SearchTextField from "../../../../../../components/wizard/SearchTextField";
import LoadingDialog from "src/components/modals/MoveLocationsModal/LoadingDialog";
import AddItemFlow from "./AddItemFlow";
import ReturnItemsModal from "./ReturnItemsModal";
import OrderAdjustmentsModal from "./OrderAdjustmentsModal";
import useOrderData from "src/views/inventory/requests/hooks/useOrderData";
import MasterScanOrEnterModal from "../../MasterScanOrEnterModal";
import GroupedAssetScanModal from "./GroupedAssetScanModal";
import useAuth from "src/hooks/useAuth";
import useHasAdditionalCharges from "src/components/capture/ReceivingWizard/hooks/useHasAdditionalCharges";
import useHasEnabledIntegration from "src/hooks/useHasEnabledIntegration";
import { nextAfterItems } from "src/utils/integrationWizardSteps";

export default function Items({ project, inventoryRequest, showRaceMode = true, verticalLayout = false }) {
  const navigate = useNavigate();
  const params = useQueryParams();
  const assetId = params.get("assetId");
  const inventoryItemId = params.get("inventoryItemId");
  const recreatingItem = params.get("recreatingItem") === "1";
  const returnItemsOpen = params.get("returnItems") === "1";
  const adjustmentsOpen = params.get("adjustItems") === "1";
  const inventoryItems = inventoryRequest?.inventoryItems || [];
  const [showSearch, setShowSearch] = useState(false);
  const [openAddItemsModal, setOpenAddItemsModal] = useState(false);
  const [openMasterScanOrEnterModal, setOpenMasterScanOrEnterModal] = useState(false);
  const [openBillingQuantitiesModal, setOpenBillingQuantitiesModal] = useState(false);
  const [selectedPlaceholderItem, setSelectedPlaceholderItem] = useState(null);
  const { user } = useAuth();
  const { settings } = user.company;
  const sortedItems = useMemo(() => {
    return orderBy(inventoryItems, v => v.rackLocations[0]?.warehouse_locations?.[0]?.location_name || "");
  }, [inventoryItems]);

  const refetchOrderData = useOrderData(inventoryRequest?.id, "inventoryRequest");

  const someItemsArePlaceholders = inventoryItems.some(item => item.placeholderForGroupedAsset);

  const { searchTerm, filteredItems, handleSearchChange } = useInventoryItemsFilter(sortedItems);
  const anyItemPicked = sortedItems.some(item => item.pickedStatus);
  const billingType = useMemo(() => getOutboundBillingType(project), [project]);
  const hasAdditionalCharges = useHasAdditionalCharges(project, "outbound");
  const { hasIntegration } = useHasEnabledIntegration(project);
  const warehouses = inventoryRequest?.warehousesFromInventoryItems || [];
  const allWarehousesExcluded =
    warehouses.length > 0 && warehouses.every(w => w.excludeFromBilling);

  const renderActions = value => <ItemActions value={value} warehouses={warehouses} />;

  const renderInfo = value => {
    if (value.placeholderForGroupedAsset) {
      const placeholderDesc = value.asset?.description?.toLowerCase();
      const addedCount = inventoryItems.filter(item =>
        !item.placeholderForGroupedAsset &&
        item.asset?.description?.toLowerCase() === placeholderDesc
      ).length;
      return <ItemInfo value={value} remainingCount={value.quantity - addedCount} />;
    }
    return <ItemInfo value={value} />;
  };

  const handleSelect = value => {
    if (value.placeholderForGroupedAsset) {
      setSelectedPlaceholderItem(value);
      return;
    }
    const pathname = window.location.pathname;
    const params = new URLSearchParams(location.search);
    params.set("inventoryItemId", value.id);
    params.set("assetId", value.asset.id);
    navigate({ pathname, search: params.toString() });
  };

  const handleClose = () => {
    const params = new URLSearchParams(location.search);
    params.delete("assetId");
    params.delete("inventoryItemId");
    navigate({ search: params.toString() });
  };

  const handleNextItem = () => {
    const params = new URLSearchParams(location.search);
    const currentIndex = sortedItems.findIndex(v => v.id === inventoryItemId);
    const nextIndex = currentIndex >= 0 ? currentIndex + 1 : 0;
    const nextItem = sortedItems[nextIndex];

    if (!nextItem) {
      handleClose();
      return;
    }

    params.set("inventoryItemId", nextItem.id);
    params.set("assetId", nextItem.asset.id);
    navigate({ search: params.toString() });
  };

  const isAllowedOutboundBillingType = [
    BY_THE_PALLET,
    BY_THE_PALLET_WAREHOUSE_BUILT,
    BY_THE_UNIT_ITEM,
    BY_THE_VAULT,
    TIME_AND_MATERIALS_BLENDED,
    TIME_AND_MATERIALS_ITEMIZED,
  ].includes(billingType);
  const handleContinue = () => {
    if (isAllowedOutboundBillingType && !allWarehousesExcluded) {
      setOpenBillingQuantitiesModal(true);
    } else {
      handleComplete();
    }
  };
  const handleSaveBillingQuantities = () => {
    setOpenBillingQuantitiesModal(false);
    handleComplete();
  };

  const handleBack = () => {
    const params = new URLSearchParams(window.location.search);
    params.set("step", "details");
    navigate({ search: params.toString() });
  };

  const handleComplete = () => {
    const params = new URLSearchParams(window.location.search);
    params.delete("assetId");
    params.delete("inventoryItemId");
    params.set(
      "step",
      nextAfterItems({
        hasIntegration,
        hasCharges: hasAdditionalCharges && !allWarehousesExcluded,
        chargeStep: "charges",
        fallbackStep: "photos",
      })
    );
    navigate({ search: params.toString() });
  };

  const handleToggleSearch = () => {
    if (showSearch) {
      handleSearchChange({ target: { value: "" } });
    }
    setShowSearch(!showSearch);
  };

  const handleCloseAddItemsModal = async () => {
    try {
      await refetchOrderData();
    } finally {
      setOpenAddItemsModal(false);
    }
  };

  const updateSearchParams = update => {
    const nextParams = new URLSearchParams(window.location.search);
    update(nextParams);
    navigate({ search: nextParams.toString() });
  };

  const handleOpenAdjustments = () => {
    updateSearchParams(params => params.set("adjustItems", "1"));
  };

  const handleCloseAdjustments = () => {
    updateSearchParams(params => params.delete("adjustItems"));
  };

  const handleAddItemsFromAdjustments = () => {
    updateSearchParams(params => params.delete("adjustItems"));
    setOpenAddItemsModal(true);
  };

  const handleReturnItemsFromAdjustments = () => {
    updateSearchParams(params => {
      params.delete("adjustItems");
      params.set("returnItems", "1");
    });
  };

  const handleCloseReturnItems = () => {
    updateSearchParams(params => params.delete("returnItems"));
  };

  const handleCloseGroupedAssetModal = async () => {
    setSelectedPlaceholderItem(null);
    await refetchOrderData();
  };

  const processNewItem = variables => ({ ...variables, pickedStatus: true });
  const handleMasterScanComplete = async () => {
    await refetchOrderData();
  };
  const renderHeaderInfo = () => {
    if (settings.scan_out_modal && !someItemsArePlaceholders) {
      const allItemsPicked = sortedItems.every(item => item.pickedStatus);
      const isDisabled = inventoryRequest?.vaStatus?.picked || inventoryRequest?.vaStatus?.completed || allItemsPicked;
      return (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setOpenMasterScanOrEnterModal(true)}
          disabled={isDisabled}
          sx={{
            ...(isDisabled && {
              backgroundColor: '#e0e0e0 !important',
              color: '#9e9e9e',
              cursor: 'not-allowed',
            })
          }}
        >
          Scan Out
        </Button>
      );
    }
    return getWO(inventoryRequest);
  };
  return (
    <>
      <Box display="flex" flexDirection="column" justifyContent="space-between" minHeight="100%">
        <Header
          title={
            <Box display="flex" alignItems="center" gap={1}>
              <span>Outbound Items</span>
              <Badge
                badgeContent={inventoryItems.length || 0}
                max={999999}
                sx={{
                  '& .MuiBadge-badge': {
                    position: 'relative',
                    transform: 'none',
                    backgroundColor: 'transparent',
                    color: '#666',
                    fontSize: '14px',
                    height: '24px',
                    minWidth: '24px',
                    borderRadius: '50%',
                    fontWeight: 500,
                    border: '1.5px solid #999',
                  }
                }}
              />
            </Box>
          }
          actions={<SearchButton open={showSearch} onClick={handleToggleSearch} />}
          info={renderHeaderInfo()}
          sticky
        />
        {showSearch && (
          <Box
            px={2}
            py={1.5}
            sx={{
              position: "sticky",
              top: 64,
              backgroundColor: "white",
              zIndex: 9,
            }}
          >
            <SearchTextField value={searchTerm} onChange={handleSearchChange} />
          </Box>
        )}
        <Box p={2}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Alert
              severity="info"
              icon={false}
              role="button"
              tabIndex={0}
              onClick={handleOpenAdjustments}
              onKeyDown={event => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleOpenAdjustments();
                }
              }}
              sx={{
                alignSelf: "stretch",
                position: "sticky",
                top: showSearch ? 128 : 64,
                zIndex: 8,
                mb: 1.5,
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "#d8f1fb",
                },
                "&:focus-visible": {
                  outline: "3px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              <Typography fontWeight={500}>Need to update this order?</Typography>
              <Typography variant="body2">
                Add or return items here.
              </Typography>
            </Alert>
            {sortedItems.length === 0 ? (
              <Box pt={7} display="flex" justifyContent="center">
                No items to pick
              </Box>
            ) : filteredItems.length === 0 && searchTerm ? (
              <Box py={5} mt={3} display="flex" justifyContent="center" width="100%">
                <Typography variant="body2" color="textSecondary">
                  No items found matching "{searchTerm}"
                </Typography>
              </Box>
            ) : (
              <Box width="100%" minHeight={300}>
                {filteredItems.map(value => {
                  const isGroupedAsset = value.asset?.type === "GroupedAsset";
                  const isPlaceholderItem = value.placeholderForGroupedAsset;
                  const isSelectable = isPlaceholderItem || !isGroupedAsset;
                  return (
                    <Box
                      key={value.id}
                      sx={{
                        ...(isPlaceholderItem && !someItemsArePlaceholders && {
                          opacity: 0.7,
                          cursor: "default",
                        })
                      }}
                    >
                      <Item
                        value={value}
                        project={project}
                        info={renderInfo(value)}
                        actions={renderActions(value)}
                        clickableActions={true}
                        onClick={isSelectable ? () => handleSelect(value) : undefined}
                        isGroupedAsset={isPlaceholderItem}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
            <Box mt={4}>
              {assetId && project && (
                <ItemDetails
                  id={assetId}
                  project={project}
                  inventoryRequest={inventoryRequest}
                  inventoryItem={sortedItems.find(v => v.id === inventoryItemId)}
                  onClose={handleClose}
                  onNextItem={handleNextItem}
                />
              )}
            </Box>
          </Box>
        </Box>
        <Box mt={2}>
          <Footer
            step={1}
            steps={4}
            startTime={inventoryRequest?.pickingStartTime}
            completeTime={inventoryRequest?.pickingCompleteTime}
            showRaceMode={!verticalLayout && showRaceMode}
          >
            <div style={{ width: verticalLayout ? "100%" : "auto" }}>
              {!verticalLayout && (
                <Button onClick={handleBack} variant="outlined" sx={{ mr: 1 }}>
                  Back
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                onClick={handleContinue}
                disabled={!anyItemPicked}
                fullWidth={verticalLayout}
              >
                {verticalLayout ? "Next Step" : "Next"}
              </Button>
            </div>
          </Footer>
        </Box>
      </Box>
      {openBillingQuantitiesModal && (
        <BillingQuantitiesModal
          type="Outbound"
          billingType={billingType}
          shipment={inventoryRequest}
          palletTypes={project?.palletTypes}
          unitTypes={project?.unitTypes}
          onSave={handleSaveBillingQuantities}
          onClose={() => setOpenBillingQuantitiesModal(false)}
        />
      )}
      {openAddItemsModal && (
        <AddItemFlow
          projectId={project?.id}
          inventoryRequestId={inventoryRequest?.id}
          processNewItem={processNewItem}
          onClose={handleCloseAddItemsModal}
        />
      )}
      {adjustmentsOpen && (
        <OrderAdjustmentsModal
          onClose={handleCloseAdjustments}
          onAddItems={handleAddItemsFromAdjustments}
          onReturnItems={handleReturnItemsFromAdjustments}
        />
      )}
      {returnItemsOpen && (
        <ReturnItemsModal
          inventoryRequest={inventoryRequest}
          onClose={handleCloseReturnItems}
          onReturned={refetchOrderData}
        />
      )}
      {recreatingItem && <LoadingDialog onClose={() => {}} />}
      {openMasterScanOrEnterModal && (
        <MasterScanOrEnterModal
          inventoryRequest={inventoryRequest}
          inventoryItems={sortedItems}
          onClose={() => setOpenMasterScanOrEnterModal(false)}
          onScanComplete={handleMasterScanComplete}
        />
      )}
      {selectedPlaceholderItem && (
        <GroupedAssetScanModal
          open={true}
          inventoryItem={selectedPlaceholderItem}
          projectId={project?.id}
          inventoryRequestId={inventoryRequest?.id}
          existingItems={inventoryItems.filter(item =>
            !item.placeholderForGroupedAsset &&
            item.asset?.description?.toLowerCase() === selectedPlaceholderItem.asset?.description?.toLowerCase()
          )}
          onClose={handleCloseGroupedAssetModal}
        />
      )}
    </>
  );
}
